/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCustomerConfig } from "~/context/CustomerConfig";
import { CurrencyLSKeys } from "~/context/CustomerConfig/utils";
import { getUserLocalesInternal } from "../locale";
import { abbreviateNumber } from "../numberAbbreviation";
import { Maybe } from "../types";
import {
  currencies,
  Currency,
  CurrencyType as CurrencyType,
} from "./currencies";
import { applyRate } from "./exchange";

type SignDisplay = "always" | "auto" | "exceptZero" | "never";

const moneyFormatter = (
  currency: string,
  signDisplay: SignDisplay = "auto",
  options: Intl.NumberFormatOptions = {}
) =>
  new Intl.NumberFormat(getUserLocalesInternal(), {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    signDisplay,
    ...options,
  });

function formatMoneyValue(value: number | string, currency: string) {
  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" && value <= 0) {
    // To avoid -0
    return moneyFormatter(currency, "exceptZero").format(value);
  }

  return moneyFormatter(currency).format(value);
}

type MoneyInput = number | string | null | undefined;

export function FormatMoney({
  value,
  currency,
}: {
  value: MoneyInput;
  currency: string;
}) {
  const { config } = useCustomerConfig();
  const isApplyRateEnabled = !config?.isDefaultPriority;

  const finalValue = formatMoneyWithoutSymbol(
    value,
    currency,
    isApplyRateEnabled
  );

  return <>{formatMoneyValue(finalValue, currency)}</>;
}

export const formatMoney = (
  value: MoneyInput,
  currency: string = Currency.Eur,
  applyRateEnabled = true
) => {
  // Bugfix: For default currency we lock apply rate (when default currencies are set then lock apply rate).
  const isDefaultPriority =
    localStorage.getItem(CurrencyLSKeys.DEFAULT_PRIORITY) === "true";
  const isApplyRateEnabled = !isDefaultPriority && applyRateEnabled;

  const finalValue = formatMoneyWithoutSymbol(
    value,
    currency,
    isApplyRateEnabled
  );

  return formatMoneyValue(finalValue, currency);
};

export const getCurrencySymbol = (isoCode: string | undefined) => {
  if (isoCode === undefined) {
    return "";
  }

  return (
    currencies.find((current: CurrencyType) => current.iso === isoCode)
      ?.symbol ?? ""
  );
};

export const formatMoneyLabel = (
  n: number | { valueOf: () => number },
  currency: string
) => `${getCurrencySymbol(currency)}  ${abbreviateNumber(n as number)}`;

type ICost = {
  forecast?: Maybe<string>;
  currentMonth?: Maybe<string>;
  previousMonth?: Maybe<string>;
  originalCost?: Maybe<{
    currentMonth?: Maybe<string>;
    previousMonth?: Maybe<string>;
  }>;
};

export const formatCostObject = (cost: ICost, currency: string): ICost => {
  return {
    forecast: formatMoneyWithFallback(cost.forecast, currency),
    currentMonth: formatMoney(cost.currentMonth, currency),
    previousMonth: formatMoney(cost.previousMonth, currency),
    originalCost: {
      currentMonth: formatMoney(cost.originalCost?.currentMonth, currency),
    },
  };
};

export const formatMoneyWithoutSymbol = (
  value: MoneyInput,
  currency: string = Currency.Eur,
  applyRateEnabled = true
) => {
  if (value == null) {
    return "";
  }

  // Avoid formatting values that are already formatted (contain currency symbols), this leads to unexpected results
  if (typeof value === "string" && onlyNumbers(value) === false) {
    return value;
  }

  const parsedValue = typeof value === "number" ? value : parseFloat(value);

  if (isNaN(parsedValue) && typeof value === "string") {
    return value;
  }

  return applyRateEnabled ? applyRate(parsedValue, currency) : parsedValue;
};

function onlyNumbers(input: string) {
  return /^-?[\d,.]+$/.test(input);
}

export function formatMoneyWithFallback(
  value: Maybe<number | string> | undefined,
  currency: string,
  fallback = "N/A"
): string {
  // At the start of the month forecasts are not ready (and possibly other costs), so we get “-1” from BE
  if (value == null || Number(value) === -1) {
    return fallback;
  }

  return formatMoney(value, currency);
}
